import { bool, node, number, oneOf, oneOfType, string } from "prop-types";
import { createContext, forwardRef, useContext, useMemo, useRef } from "react";

import styled, { useTheme } from "styled-components";
import { backgroundColor, borderRadius, hoverFocus } from "theme";

import FnrTypography from "FnrTypography";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrTappyShrink from "UI/FnrTappyShrink";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";

import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";

import { useFontType, useIsBold, useTextColor } from "./stateHooks";

const FnrTextButtonContext = createContext();

/**
 * @typedef FnrTextButtonContext
 * @type {object}
 * @property {string} icon
 * @property {import("react").ReactNode} children
 * @property {boolean} isLegacy
 * @property {boolean} isHoverFocused
 * @property {size} fontSize
 * @property {boolean} emphasized
 * @property {boolean} isDark
 * @property {boolean} subtleHover
 * @property {color} color
 * @property {color} hoverColor
 * @property {boolean} noBackgroundColor
 * @property {boolean} isLoading
 * @property {boolean} noWrap
 * @property {boolean} noBorderRadius
 * @property {boolean} scaleContentOnly
 * @property {"left"|"right"|"top"|"bottom"} iconSide
 */

/**
 * @returns {FnrTextButtonContext}
 */
const useFnrTextButtonContext = () => useContext(FnrTextButtonContext);

const fnrTextPadding = ({
  $iconSide,
  $reducedYPadding,
  $reducedXPadding,
  $hasIcon,
}) => {
  const yPadding = $reducedYPadding ? "4px" : "8px";
  const xPaddingIsh = $reducedXPadding ? 0 : 4;
  if (!$hasIcon) return `padding: ${yPadding};`;
  if ($iconSide === "right")
    return `padding: ${yPadding} ${xPaddingIsh}px ${yPadding} ${
      xPaddingIsh + 6
    }px;`;
  return `padding: ${yPadding} ${xPaddingIsh + 8}px ${yPadding} ${
    xPaddingIsh + 2
  }px;`;
};

const fnrTextMargin = ({ $iconSide }) => {
  if ($iconSide === "right") return "margin-right: -4px; margin-left: -10px;";
  return "margin-left: -6px;";
};

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  ${fnrTextPadding}
  ${fnrTextMargin}
  z-index: 1;
  ${borderRadius}
  ${hoverFocus}
  ${backgroundColor}
  transition: background-color 300ms;
`;

const TypographyRelay = () => {
  const {
    children,
    fontSize,
    noWrap,
    textColor: textColorArg,
    isHoverFocused,
  } = useFnrTextButtonContext();
  const fontType = useFontType();
  const bold = useIsBold();
  const textColor = useTextColor();

  if (typeof children === "string")
    return (
      <FnrTypography
        fontSize={fontSize}
        type={fontType}
        color={isHoverFocused ? textColor : textColorArg || textColor}
        bold={bold}
        noWrap={noWrap}
      >
        {children}
      </FnrTypography>
    );
  return children;
};

const IconRelay = () => {
  const { icon, isLoading, iconSide, iconSize, iconYCorrection } =
    useFnrTextButtonContext();
  const textColor = useTextColor();

  if (!icon) return null;
  const bigger = iconSide === "bottom" || iconSide === "top";
  return (
    <FnrContainedIcon
      size={iconSize || (bigger ? "xl" : "md")}
      isLoading={isLoading}
      icon={icon}
      color={textColor}
      yCorrection={iconYCorrection}
    />
  );
};

const GapRelay = ({ children }) => {
  const { iconSide, gap } = useFnrTextButtonContext();
  if (iconSide === "left" || iconSide === "right")
    return (
      <FnrHorizontalGap gap={gap || 0} reverse={iconSide === "right"}>
        {children}
      </FnrHorizontalGap>
    );

  if (iconSide === "top" || iconSide === "bottom") {
    return (
      <FnrVerticalGap
        gap={gap || 4}
        reverse={iconSide === "bottom"}
        alignItems="center"
      >
        {children}
      </FnrVerticalGap>
    );
  }
  console.warn(`iconSide of "${iconSide}" is not a thing.`);
  return null;
};

GapRelay.propTypes = { children: node };

const FnrTextButton = forwardRef(
  /**
   *
   * @param {object} param0
   * @param {boolean} param0.show
   * @param {string} param0.icon
   * @param {"left"|"right"|"top"|"bottom"} param0.iconSide
   * @param {boolean} param0.isLegacy - uses legacy fonts and colors when true.
   * @param {size} param0.fontSize
   * @param {size} param0.iconSize
   * @param {boolean} param0.emphasized - add some OOMF!
   * @param {boolean} param0.subtleHover - text stays same color, background is .2 opacity.
   * @param {boolean} param0.isDark - make it look good on a dark background.
   * @param {color} param0.color - use whatever color you want!
   * @param {color} param0.textColor - text color falls back to color if undefined
   * @param {color} param0.hoverColor - overrides the default hover color of the text.
   * @param {color} param0.hoverBackgroundColor
   * @param {boolean} param0.noBackgroundColor - turns off all background color.
   * @param {boolean} param0.reducedYPadding - less padding on top an bottom.
   * @param {boolean} param0.reducedXPadding - less padding on right and left.
   * @param {boolean} param0.noWrap
   * @param {boolean} param0.fullWidth
   * @param {number} param0.gap
   * @param {number} param0.iconYCorrection - some icons in certain sizes need tweak on the y axis. Use this to get it perfectly aligned.
   * @param {boolean} param0.active - sets the button to the active state (full background color, even if subtleHover)
   * @param {boolean} param0.scaleContentOnly - shrinks the content of button instead of the whole button
   * @param {string} param0.justifyContent - aligns the content of the button
   * @returns
   */
  (
    {
      icon,
      iconSide = "left",
      children,
      isLegacy,
      isLoading,
      emphasized,
      fontSize = emphasized ? "md" : "xs",
      iconSize,
      subtleHover,
      isDark,
      color,
      hoverColor,
      noBackgroundColor,
      reducedYPadding,
      reducedXPadding,
      noWrap,
      fullWidth,
      noBorderRadius,
      gap,
      iconYCorrection,
      textColor,
      hoverBackgroundColor: hoverBackgroundColorArg,
      active,
      scaleContentOnly,
      justifyContent,
      ...rest
    },
    passedInRef
  ) => {
    const localRef = useRef(null);
    const ref = passedInRef || localRef;
    const isHoverFocused = useIsHoverFocused(ref);
    const { colors, palette } = useTheme();

    const hoverBackgroundColor = useMemo(() => {
      if (noBackgroundColor) return undefined;
      return (
        palette[hoverBackgroundColorArg || color] ||
        (isLegacy ? colors.textSecondary : palette.taupe)
      );
    }, [
      noBackgroundColor,
      palette,
      hoverBackgroundColorArg,
      color,
      isLegacy,
      colors.textSecondary,
    ]);

    if (!children) return null;
    return (
      <FnrTextButtonContext.Provider
        value={{
          icon,
          children,
          isLegacy,
          isHoverFocused,
          fontSize,
          iconSize,
          emphasized,
          isDark,
          subtleHover,
          color,
          textColor,
          hoverColor,
          noBackgroundColor,
          isLoading,
          noWrap,
          iconSide,
          gap,
          iconYCorrection,
          active,
          justifyContent
        }}
      >
        <FnrHorizontalGap fullWidth={fullWidth} justifyContent={justifyContent}>
          <FnrTappyShrink
            data-cy="FnrTextButton_Container"
            fullWidth={fullWidth}
            muted={scaleContentOnly}
          >
            <SFnrButtonWrapper
              ref={ref}
              $hoverFocusBackgroundColor={`${hoverBackgroundColor}${
                subtleHover && !active ? "33" : ""
              }`}
              $iconSide={iconSide}
              $hasIcon={Boolean(icon)}
              $reducedYPadding={reducedYPadding}
              $reducedXPadding={reducedXPadding}
              fullWidth={fullWidth}
              $noBorderRadius={noBorderRadius}
              $backgroundColor={active ? hoverBackgroundColorArg : undefined}
              {...rest}
            >
              <FnrTappyShrink muted={!scaleContentOnly} fullWidth alignLeft>
                <GapRelay>
                  <IconRelay />
                  <TypographyRelay>{children}</TypographyRelay>
                </GapRelay>
              </FnrTappyShrink>
            </SFnrButtonWrapper>
          </FnrTappyShrink>
        </FnrHorizontalGap>
      </FnrTextButtonContext.Provider>
    );
  }
);

FnrTextButton.propTypes = {
  icon: string,
  children: oneOfType([string, node]),
  isLoading: bool,
  isLegacy: bool,
  fontSize: string,
  iconSize: string,
  iconSide: oneOf(["left", "right", "top", "bottom"]),
  emphasized: bool,
  isDark: bool,
  subtleHover: bool,
  color: string,
  hoverColor: string,
  noBackgroundColor: bool,
  reducedYPadding: bool,
  reducedXPadding: bool,
  noWrap: bool,
  fullWidth: bool,
  noBorderRadius: bool,
  gap: number,
  iconYCorrection: number,
  textColor: string,
  hoverBackgroundColor: string,
  active: bool,
  scaleContentOnly: bool,
  justifyContent: string,
};

export { useFnrTextButtonContext };
export default FnrTextButton;